import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Sentry from "@sentry/react-native";
import * as Crypto from "expo-crypto";
import { signInWithCustomToken } from "firebase/auth";
import {
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type Unsubscribe
} from "firebase/firestore";

import type { DeviceRegistrationMetadata } from "@recital-master/shared";

import { getClientDeviceIdentity } from "@/devices/deviceIdentity";
import { firebaseAuth, firestore } from "@/firebase/client";

const STORAGE_KEY = "recital-master.loginRequestId.v1";
const COMPLETION_SLOW_MS = 10_000;
const CONSUMPTION_RETRY_DELAYS_MS = [0, 1_000, 3_000, 10_000] as const;
export type LoginRequestDetails = {
  requestId: string;
  device: DeviceRegistrationMetadata;
};

export async function createLoginRequestDetails(): Promise<LoginRequestDetails> {
  const bytes = await Crypto.getRandomBytesAsync(16);
  const requestId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  const device = await getClientDeviceIdentity();
  const { deviceId: _deviceId, ...metadata } = device;
  return {
    requestId,
    device: metadata
  };
}

export async function storeCurrentLoginRequestId(requestId: string): Promise<void> {
  await AsyncStorage.setItem(STORAGE_KEY, requestId);
}

export async function readCurrentLoginRequestId(): Promise<string | null> {
  return AsyncStorage.getItem(STORAGE_KEY);
}

export async function clearCurrentLoginRequestId(): Promise<void> {
  await AsyncStorage.removeItem(STORAGE_KEY);
}

export async function consumeLoginRequest(requestId: string): Promise<void> {
  if (!firestore) return;
  const requestRef = doc(firestore, "loginRequests", requestId);
  try {
    await updateDoc(requestRef, {
      status: "consumed",
      token: deleteField(),
      consumedAt: serverTimestamp()
    });
  } catch (error) {
    // Two waiting/restored tabs can finish at nearly the same time. Treat the
    // other tab's successful consumption as success instead of showing an
    // error after authentication already worked.
    const current = await getDoc(requestRef);
    if (current.get("status") !== "consumed") throw error;
  }
  await clearCurrentLoginRequestId();
}

async function consumeLoginRequestWithRetry(requestId: string): Promise<void> {
  let lastError: unknown;
  for (const delayMs of CONSUMPTION_RETRY_DELAYS_MS) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      await consumeLoginRequest(requestId);
      return;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

/**
 * The requesting device is already authenticated when this begins. Removing
 * the short-lived handoff token is important, but a slow Firestore write must
 * never hold the authenticated family behind the login curtain.
 *
 * Keep the local request ID until cleanup succeeds so a reload can recover and
 * retry an approved-but-not-yet-consumed handoff.
 */
export function consumeLoginRequestInBackground(requestId: string): void {
  void consumeLoginRequestWithRetry(requestId).catch((error) => {
    Sentry.captureException(error, {
      tags: {
        area: "authentication",
        operation: "consume-login-request"
      }
    });
  });
}

/**
 * Complete the requesting device's half of an email-link handoff.
 *
 * Every surface that sends a sign-in link must stay attached to the same
 * request until approval, rather than leaving families to refresh or repeat
 * the login flow manually.
 */
export function subscribeToLoginRequestApproval(
  requestId: string,
  callbacks: {
    onFinishing: () => void;
    onSlow?: () => void;
    onComplete: () => void | Promise<void>;
    onError: (error: unknown) => void;
  }
): Unsubscribe | undefined {
  const db = firestore;
  const auth = firebaseAuth;
  if (!db || !auth) return undefined;

  let completing = false;
  return onSnapshot(
    doc(db, "loginRequests", requestId),
    (snapshot) => {
      const data = snapshot.data();
      if (
        completing ||
        data?.status !== "approved" ||
        typeof data.token !== "string"
      ) {
        return;
      }

      completing = true;
      callbacks.onFinishing();
      const slowTimer = setTimeout(() => callbacks.onSlow?.(), COMPLETION_SLOW_MS);
      void signInWithCustomToken(auth, data.token)
        .then(() => {
          clearTimeout(slowTimer);
          consumeLoginRequestInBackground(requestId);
          return callbacks.onComplete();
        })
        .catch((error) => {
          clearTimeout(slowTimer);
          completing = false;
          callbacks.onError(error);
        });
    },
    callbacks.onError
  );
}
